import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle16, ValidationCheckBold16 } from '../../icons';
import { cn } from '../../utils/cn';

type CompButtonFormActionState = 'initial' | 'loading' | 'success' | 'feedback' | 'error';

interface CompButtonFormActionContentProps {
  actionsStates?: {
    initial?: boolean;
    loading?: boolean;
    feedback?: boolean;
    success?: boolean;
    error?: boolean;
  };
  ariaLabelErrorState?: string;
  ariaLabelFeedbackState?: string;
  ariaLabelLoadingState?: string;
  ariaLabelSuccessState?: string;
  buttonActionsContext?: {
    errorText?: React.ReactNode;
    successText?: React.ReactNode;
    feedbackText?: React.ReactNode;
    initialText?: React.ReactNode;
    loadingText?: React.ReactNode;
  };
  content: React.ReactNode;
  contentTransformClass?: string;
  formActionSlots?: {
    initial?: React.ReactNode;
    loading?: React.ReactNode;
    success?: React.ReactNode;
    feedback?: React.ReactNode;
    error?: React.ReactNode;
  };
}

const FORM_ACTION_FEEDBACK_DELAY = 2400;
const FORM_ACTION_STATE_TRANSITION_DURATION = 500;

const CompButtonFormActionContent = ({
  actionsStates,
  ariaLabelErrorState,
  ariaLabelFeedbackState,
  ariaLabelLoadingState,
  ariaLabelSuccessState,
  buttonActionsContext,
  content,
  contentTransformClass,
  formActionSlots,
}: CompButtonFormActionContentProps) => {
  const [formActionFeedbackVisible, setFormActionFeedbackVisible] = useState(
    Boolean(actionsStates?.feedback)
  );
  const [leavingFormActionState, setLeavingFormActionState] =
    useState<CompButtonFormActionState | null>(null);
  const previousFormActionStateRef = useRef<CompButtonFormActionState | undefined>(undefined);

  useEffect(() => {
    if (actionsStates?.success) {
      setFormActionFeedbackVisible(false);

      const feedbackTimer = setTimeout(() => {
        setFormActionFeedbackVisible(true);
      }, FORM_ACTION_FEEDBACK_DELAY);

      return () => clearTimeout(feedbackTimer);
    }

    setFormActionFeedbackVisible(Boolean(actionsStates?.feedback));
  }, [actionsStates?.feedback, actionsStates?.success]);

  const resolvedButtonActionsContext = {
    errorText: buttonActionsContext?.errorText ?? ariaLabelErrorState ?? 'Error',
    successText: buttonActionsContext?.successText ?? ariaLabelSuccessState ?? 'Success',
    feedbackText: buttonActionsContext?.feedbackText ?? ariaLabelFeedbackState ?? 'Feedback',
    initialText: buttonActionsContext?.initialText,
    loadingText: buttonActionsContext?.loadingText ?? ariaLabelLoadingState ?? 'Loading...',
  };
  const formActionState: CompButtonFormActionState = actionsStates?.error
    ? 'error'
    : formActionFeedbackVisible
      ? 'feedback'
      : actionsStates?.success
        ? 'success'
        : actionsStates?.loading
          ? 'loading'
          : 'initial';
  const showFormActionSuccessSurface =
    formActionState === 'success' || formActionState === 'feedback';

  useEffect(() => {
    const previousFormActionState = previousFormActionStateRef.current;
    previousFormActionStateRef.current = formActionState;

    if (!previousFormActionState || previousFormActionState === formActionState) {
      return;
    }

    setLeavingFormActionState(previousFormActionState);

    const transitionTimer = setTimeout(() => {
      setLeavingFormActionState(currentState =>
        currentState === previousFormActionState ? null : currentState
      );
    }, FORM_ACTION_STATE_TRANSITION_DURATION);

    return () => clearTimeout(transitionTimer);
  }, [formActionState]);

  const renderFormActionStateContent = (state: CompButtonFormActionState) => {
    const stateHasSlotOverride = Boolean(
      formActionSlots && Object.prototype.hasOwnProperty.call(formActionSlots, state)
    );
    const stateSlotContent = formActionSlots?.[state];

    if (stateHasSlotOverride) {
      return stateSlotContent;
    }

    if (state === 'loading') {
      return <span>{resolvedButtonActionsContext.loadingText}</span>;
    }

    if (state === 'success') {
      return (
        <ValidationCheckBold16
          aria-hidden="true"
          className="h-4 w-4"
          data-testid="comp-button-form-action-success-icon"
        />
      );
    }

    if (state === 'feedback') {
      return <span>{resolvedButtonActionsContext.feedbackText}</span>;
    }

    if (state === 'error') {
      return (
        <>
          <AlertTriangle16
            aria-hidden="true"
            className="h-4 w-4"
            data-testid="comp-button-form-action-error-icon"
          />
          <span>{resolvedButtonActionsContext.errorText}</span>
        </>
      );
    }

    if (resolvedButtonActionsContext.initialText) {
      return <span>{resolvedButtonActionsContext.initialText}</span>;
    }

    return content;
  };

  return (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-0 z-0 origin-top bg-velux-green-800 transition-transform duration-700 ease-out',
          showFormActionSuccessSurface ? 'scale-y-100' : 'scale-y-0'
        )}
        data-testid="comp-button-form-action-overlay"
      />
      <span
        className={cn(
          'relative z-10 grid min-w-40 place-items-center text-center',
          contentTransformClass
        )}
      >
        {leavingFormActionState && leavingFormActionState !== formActionState ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute inset-0 flex items-center justify-center gap-2 text-center animate-comp-button-form-action-leave',
              contentTransformClass
            )}
          >
            {renderFormActionStateContent(leavingFormActionState)}
          </span>
        ) : null}
        <span
          className={cn(
            'flex items-center justify-center gap-2 text-center',
            leavingFormActionState &&
              leavingFormActionState !== formActionState &&
              'absolute inset-0 animate-comp-button-form-action-enter',
            contentTransformClass
          )}
        >
          {renderFormActionStateContent(formActionState)}
        </span>
      </span>
    </>
  );
};

export { CompButtonFormActionContent };
